// @flow
/**
 * Builds API payloads without UI sentinel values.
 * The API requires `petBreedId`; the backend maps it to `breedId`.
 */
import {
  formatDateDB,
  toMoment,
} from '@fortress-technology-solutions/fortress-component-library/utils';

import { SERVICE_ANIMAL_YES } from './fields';
import type {
  PetFormValues,
  PetPayloadData,
  PetPayloadItem,
  PetViewModel,
  SelectedFee,
} from '../types';

// Omit blank optional text because the API rejects it.
const sanitizeOptionalString = (value: ?string): string | void => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return undefined;
  return trimmed;
};

const sanitizeSelect = (value: ?string): string => value || '';

/** Converts a nonblank weight to a number. */
const sanitizeWeight = (value: ?(string | number)): number | void => {
  if (value === null || value === undefined) return undefined;
  // Prevent whitespace from converting to zero.
  const trimmed = typeof value === 'string' ? value.trim() : value;
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** Converts the Yes/No selection or API value to a boolean. */
const sanitizeServiceAnimal = (value: mixed): boolean =>
  value === true || value === SERVICE_ANIMAL_YES;

/** Formats a valid date as the selected local calendar day. */
const sanitizeDate = (value: mixed): string | void => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = toMoment(value);
  if (!parsed?.isValid()) return undefined;
  return formatDateDB(parsed, false);
};

/** Builds common pet data and omits blank optional fields. */
export const buildPetData = (values: PetFormValues): PetPayloadData => {
  const weight = sanitizeWeight(values.weight);
  const petTag = sanitizeOptionalString(values.petTag);
  const endDate = sanitizeDate(values.endDate);

  const data: PetPayloadData = {
    name: (values.name ?? '').trim(),
    petTypeId: sanitizeSelect(values.petTypeId),
    petBreedId: sanitizeSelect(values.petBreedId),
    isServiceAnimal: sanitizeServiceAnimal(values.isServiceAnimal),
    // Keep this required API field present if client validation is bypassed.
    startDate: sanitizeDate(values.startDate) ?? '',
  };

  if (weight !== undefined) data.weight = weight;
  if (petTag !== undefined) data.petTag = petTag;
  if (endDate !== undefined) data.endDate = endDate;

  return data;
};

/** Omits the outer `id` to select the create operation. */
export const buildAddPetPayload = (
  values: PetFormValues,
): Array<PetPayloadItem> => [{ type: 'pet', data: buildPetData(values) }];

/** Adds the pet and positive one-time fees in one atomic request. */
export const buildAddPetWithFeesPayload = (
  values: PetFormValues,
  selectedFees: Array<SelectedFee>,
): Array<PetPayloadItem> => [
  { type: 'pet', data: buildPetData(values) },
  ...(selectedFees ?? [])
    .filter(({ count }) => Number(count) > 0)
    .map(({ propertyFeeId, count }) => ({
      type: 'petFeeToCharge',
      data: { propertyFeeId, count: Number(count) },
    })),
];

/**
 * Uses the outer `id` to update a resident pet.
 * Sends `null` for blank fields that the user can clear.
 */
export const buildUpdateResidentPetPayload = (
  pet: PetViewModel,
  values: PetFormValues,
): Array<PetPayloadItem> => {
  const data = buildPetData(values);
  const residentPetId = pet.raw?.id;

  return [
    {
      type: 'pet',
      id: pet.id,
      data: {
        ...data,
        ...(residentPetId ? { residentPetId } : {}),
        weight: data.weight ?? null,
        petTag: data.petTag ?? null,
        endDate: data.endDate ?? null,
      },
    },
  ];
};

/**
 * Build applicant pet payloads for `updateApplicants`.
 * Use `breedId` and send all applicant arrays.
 * Send the complete adult list for validation.
 * Omit Start Date and End Date.
 * Send `null` for blank Weight and Pet Tag so an update clears the stored
 * value instead of keeping it.
 */
const buildApplicantPetData = (values: PetFormValues) => ({
  name: (values.name ?? '').trim(),
  petTypeId: sanitizeSelect(values.petTypeId),
  breedId: sanitizeSelect(values.petBreedId),
  isServiceAnimal: sanitizeServiceAnimal(values.isServiceAnimal),
  weight: sanitizeWeight(values.weight) ?? null,
  petTag: sanitizeOptionalString(values.petTag) ?? null,
});

/** Add a pet without an `id`, or update a pet with an `id`. */
export const buildApplicantPetPayload = ({
  pet,
  values,
  existingAdults,
}: {
  pet: ?PetViewModel,
  values: PetFormValues,
  existingAdults: Array<Object>,
}): Object => ({
  adults: existingAdults,
  minors: [],
  pets: [
    {
      ...(pet ? { id: pet.id } : {}),
      ...buildApplicantPetData(values),
      deleted: false,
    },
  ],
});
